'use client';
import React, { useEffect, useRef } from 'react';

const SWOT_ITEMS = [
  {
    letter: 'S',
    label: 'Strengths',
    desc: 'Internal resources, capabilities, and competitive advantages your organization already possesses.',
    color: 'rgba(52, 211, 153, 0.12)',
    border: 'rgba(52, 211, 153, 0.25)',
    textColor: '#34d399',
  },
  {
    letter: 'W',
    label: 'Weaknesses',
    desc: 'Internal limitations, gaps, and areas where your organization underperforms or lacks resources.',
    color: 'rgba(251, 113, 133, 0.12)',
    border: 'rgba(251, 113, 133, 0.25)',
    textColor: '#fb7185',
  },
  {
    letter: 'O',
    label: 'Opportunities',
    desc: 'External market conditions, trends, and openings your organization can leverage for growth.',
    color: 'rgba(96, 165, 250, 0.12)',
    border: 'rgba(96, 165, 250, 0.25)',
    textColor: '#60a5fa',
  },
  {
    letter: 'T',
    label: 'Threats',
    desc: 'External risks, competitive pressures, and environmental factors that could undermine success.',
    color: 'rgba(251, 191, 36, 0.12)',
    border: 'rgba(251, 191, 36, 0.25)',
    textColor: '#fbbf24',
  },
];

export default function OverviewSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    const el = sectionRef?.current;
    if (el) {
      el?.querySelectorAll('.reveal-up, .reveal-blur, .stagger-grid, .reveal-text-line')?.forEach((child) => observer?.observe(child));
      observer?.observe(el);
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="overview"
      className="relative w-full py-28 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
    >
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(201,168,76,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.5) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* Left: Header */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="reveal-up">
              <span className="font-mono text-xs text-accent uppercase tracking-widest">About the Guide</span>
            </div>
            <h2 className="font-manrope text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.9] reveal-up">
              <span className="block reveal-text-line">Strategic</span>
              <span className="block text-foreground/30 reveal-text-line" style={{ transitionDelay: '0.1s' }}>Clarity</span>
              <span className="block reveal-text-line" style={{ transitionDelay: '0.2s' }}>
                is a <span className="text-gold">Competitive</span>
              </span>
              <span className="block reveal-text-line" style={{ transitionDelay: '0.3s' }}>Advantage.</span>
            </h2>
            <p className="text-foreground/55 text-base leading-relaxed font-light reveal-blur max-w-md">
              In an increasingly complex and fast-changing business environment,
              this guide goes beyond definitions to show how SWOT analysis can be
              applied intentionally — to uncover hidden strengths, address critical
              weaknesses, seize emerging opportunities, and prepare for external threats.
            </p>
            <p className="text-foreground/55 text-base leading-relaxed font-light reveal-blur max-w-md">
              Designed for leaders, managers, entrepreneurs, and project teams, this
              guide walks readers through the complete SWOT process from foundational
              concepts to advanced applications.
            </p>
            <div className="reveal-up mt-2">
              <a
                href="#download"
                className="inline-flex items-center gap-2 text-accent text-sm font-medium font-mono hover:text-accent-light transition-colors border-b border-accent/30 pb-1 hover:border-accent/70"
              >
                Get your copy for $9.99
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Right: SWOT quadrant */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden stagger-grid">
              {SWOT_ITEMS?.map((item) => (
                <div
                  key={item?.letter}
                  className="swot-quadrant p-6 lg:p-8 flex flex-col gap-4 cursor-default"
                  style={{ background: 'var(--surface)' }}
                >
                  <div className="flex items-start justify-between">
                    <span
                      className="text-4xl font-manrope font-bold leading-none"
                      style={{ color: item?.textColor }}
                    >
                      {item?.letter}
                    </span>
                    <span
                      className="text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{ color: item?.textColor, background: item?.color, border: `1px solid ${item?.border}` }}
                    >
                      {item?.label}
                    </span>
                  </div>
                  <p className="text-foreground/50 text-sm leading-relaxed font-light">
                    {item?.desc}
                  </p>
                  <div
                    className="h-px w-full mt-auto"
                    style={{ background: `linear-gradient(to right, ${item?.border}, transparent)` }}
                  />
                </div>
              ))}
            </div>

            {/* Internal vs External label */}
            <div className="mt-4 grid grid-cols-2 gap-px">
              <div className="text-center py-2">
                <span className="text-xs font-mono text-foreground/30 uppercase tracking-widest">← Internal Factors</span>
              </div>
              <div className="text-center py-2">
                <span className="text-xs font-mono text-foreground/30 uppercase tracking-widest">External Forces →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}